import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Alert, Breadcrumbs, Link } from '@mui/material';
import { Home, Construction } from '@mui/icons-material';
import { useMenuConfig } from '@/hooks';
import { usePageTitle } from '@/hooks/usePageTitle';

interface PlaceholderRoute {
  key: string;
  label: string;
}

interface CategoryLeaf {
  key: string;
  label: string;
}

interface NavGroup {
  category_leaves?: CategoryLeaf[];
}

interface UiNav {
  placeholder_routes?: PlaceholderRoute[];
  groups?: Record<string, NavGroup>;
}

export const PlaceholderPage: React.FC = () => {
  const { featureKey } = useParams<{ featureKey: string }>();
  const navigate = useNavigate();
  const { data: uiNav } = useMenuConfig();

  const getFeatureLabel = (key: string, nav: UiNav | undefined): string | null => {
    if (!nav) return null;

    // Check placeholder_routes
    const placeholder = nav.placeholder_routes?.find((p) => p.key === key);
    if (placeholder) return placeholder.label;

    // Check category_leaves in all groups
    for (const group of Object.values(nav.groups || {})) {
      const leaf = group.category_leaves?.find((l) => l.key === key);
      if (leaf) return leaf.label;
    }

    return null;
  };

  const featureLabel = getFeatureLabel(featureKey || '', uiNav ?? undefined) || 'Feature';
  usePageTitle(featureLabel === 'Feature' ? 'Coming Soon' : featureLabel);

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">{featureLabel}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Construction sx={{ mr: 2, fontSize: 40, color: 'warning.main' }} />
          <Typography variant="h4" component="h1">
            {featureLabel}
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        This feature is currently under development and will be available in a future release.
      </Alert>

      <Typography variant="body1" color="text.secondary">
        The <strong>{featureLabel}</strong> functionality is part of our planned military property
        management system enhancements. Please check back later or contact your system administrator
        for more information.
      </Typography>
    </Container>
  );
};
